import React, { useEffect } from "react"
import { useNavigate } from "react-router-dom"
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    Spinner,
    Alert,
    Button,
} from "reactstrap"
import { useSelector, useDispatch } from "react-redux"
import { fetchEmpresas, setEmpresaSeleccionada } from "../../store/actions"
import logoDark from "../../assets/images/lsda-logo.png"

const SeleccionarEmpresa = () => {
    document.title = "Seleccionar Empresa | LS-DA"
    const dispatch = useDispatch()
    const navigate = useNavigate()

    const {
        empresas,
        empresasLoading,
        empresasError,
        user,
    } = useSelector(state => state.Login)

    // Cargar empresas al montar
    useEffect(() => {
        dispatch(fetchEmpresas())
    }, [dispatch])

    // Si solo hay 1 empresa, auto-seleccionar
    useEffect(() => {
        if (!empresasLoading && empresas?.length === 1) {
            handleSelectEmpresa(empresas[0])
        }
    }, [empresas, empresasLoading])

    const handleSelectEmpresa = empresa => {
        dispatch(setEmpresaSeleccionada(empresa, navigate))
    }

    return (
        <React.Fragment>
            <div className="account-pages my-5 pt-sm-5">
                <Container>
                    {/* Header */}
                    <Row className="justify-content-center mb-4">
                        <Col md={8} lg={6} xl={5} className="text-center">
                            <img src={logoDark} alt="LS-DA" height="80" className="mb-3" />
                            <h4 className="text-muted mb-1">
                                Hola, {user?.nombre || "Usuario"} 👋
                            </h4>
                            <p className="text-muted">
                                Selecciona la empresa con la que deseas trabajar
                            </p>
                        </Col>
                    </Row>

                    {/* Error */}
                    {empresasError && (
                        <Row className="justify-content-center mb-3">
                            <Col md={8} lg={6} xl={5} className="text-center">
                                <Alert color="danger">{empresasError}</Alert>
                                <Button
                                    color="primary"
                                    size="sm"
                                    onClick={() => dispatch(fetchEmpresas())}
                                    className="me-2"
                                >
                                    <i className="mdi mdi-refresh me-1" />
                                    Reintentar
                                </Button>
                                <Button
                                    color="outline-secondary"
                                    size="sm"
                                    onClick={() => navigate("/logout")}
                                >
                                    Cerrar sesión
                                </Button>
                            </Col>
                        </Row>
                    )}

                    {/* Loading */}
                    {empresasLoading && (
                        <Row className="justify-content-center">
                            <Col className="text-center">
                                <Spinner color="primary" />
                                <p className="mt-2 text-muted">Cargando empresas...</p>
                            </Col>
                        </Row>
                    )}

                    {/* Lista de empresas */}
                    {!empresasLoading && !empresasError && empresas?.length > 1 && (
                        <Row className="justify-content-center">
                            {empresas.map(empresa => (
                                <Col key={empresa.id} sm={6} md={4} lg={3} className="mb-4">
                                    <Card
                                        className="h-100 shadow-sm"
                                        style={{ cursor: "pointer", transition: "transform 0.15s, box-shadow 0.15s" }}
                                        onMouseEnter={e => {
                                            e.currentTarget.style.transform = "translateY(-4px)"
                                            e.currentTarget.style.boxShadow = "0 8px 25px rgba(0,0,0,0.15)"
                                        }}
                                        onMouseLeave={e => {
                                            e.currentTarget.style.transform = "translateY(0)"
                                            e.currentTarget.style.boxShadow = ""
                                        }}
                                        onClick={() => handleSelectEmpresa(empresa)}
                                    >
                                        <CardBody className="d-flex flex-column align-items-center text-center p-4">
                                            {empresa.logoUrl ? (
                                                <img
                                                    src={empresa.logoUrl}
                                                    alt={empresa.nombre}
                                                    style={{
                                                        width: 80,
                                                        height: 80,
                                                        objectFit: "contain",
                                                        marginBottom: 16,
                                                    }}
                                                    onError={e => {
                                                        e.target.style.display = "none"
                                                    }}
                                                />
                                            ) : (
                                                <div
                                                    style={{
                                                        width: 80,
                                                        height: 80,
                                                        borderRadius: "50%",
                                                        backgroundColor: "#556ee6",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        marginBottom: 16,
                                                        fontSize: 28,
                                                        fontWeight: "bold",
                                                        color: "#fff",
                                                    }}
                                                >
                                                    {empresa.nombre?.charAt(0)?.toUpperCase() || "E"}
                                                </div>
                                            )}
                                            <h6
                                                className="mb-1"
                                                style={{
                                                    fontSize: "0.85rem",
                                                    fontWeight: 600,
                                                    lineHeight: 1.3,
                                                }}
                                            >
                                                {empresa.nombre}
                                            </h6>
                                            <small className="text-muted">
                                                NIT: {empresa.nit}
                                                {empresa.digVerificacion
                                                    ? `-${empresa.digVerificacion}`
                                                    : ""}
                                            </small>
                                        </CardBody>
                                    </Card>
                                </Col>
                            ))}
                        </Row>
                    )}

                    {/* Sin empresas */}
                    {!empresasLoading && empresas?.length === 0 && !empresasError && (
                        <Row className="justify-content-center">
                            <Col md={8} lg={6} xl={5} className="text-center">
                                <Alert color="warning">
                                    No tienes empresas asignadas. Contacta al administrador.
                                </Alert>
                            </Col>
                        </Row>
                    )}

                    {/* Footer */}
                    <Row className="justify-content-center mt-4">
                        <Col className="text-center">
                            <p className="text-muted mb-0">
                                © {new Date().getFullYear()} LS-DA S.A.S
                            </p>
                        </Col>
                    </Row>
                </Container>
            </div>
        </React.Fragment>
    )
}

export default SeleccionarEmpresa
